import React from "react";
import {
  PROPERTY_TYPE_LABELS,
  PROPERTY_TYPES,
} from "../constants/aiPriceData";

/**
 * Component to display property form data in a structured, readable format
 * Used in PriceHistoryScreen detail view to show what data was submitted
 */
const PropertyFormDataDisplay = ({ formData, isRental = false }) => {
  if (!formData) {
    return (
      <div className="text-gray-500 italic">
        Không có thông tin chi tiết về yêu cầu
      </div>
    );
  }

  // Field label mappings
  const fieldLabels = {
    // Property type
    propertyType: "Loại BĐS",
    apartmentType: "Loại căn hộ",
    houseType: "Loại nhà",
    landType: "Loại đất",
    propertyTypeOther: "Loại BĐS",

    // Location
    address: "Địa chỉ",
    projectName: "Tên dự án",
    building: "Toà/Khu",
    floor: "Tầng/Dãy",
    apartmentCode: "Mã căn hộ",

    // Dimensions
    area: "Diện tích",
    landArea: "Diện tích đất",
    constructionArea: "Diện tích xây dựng",
    landLength: "Chiều dài đất",
    frontage: "Mặt tiền",

    // Room details
    bedrooms: "Phòng ngủ",
    bathrooms: "Phòng tắm",
    floors: "Số tầng",

    // Direction
    balconyDirection: "Hướng ban công",
    doorDirection: "Hướng cửa",
    landDirection: "Hướng đất",
    direction: "Hướng",

    // Legal & Status
    legalStatus: "Tình trạng pháp lý",
    furnitureStatus: "Tình trạng nội thất",

    // Goals
    saleGoal: "Mục tiêu",
    rentalGoal: "Mục tiêu cho thuê",

    // Rental specific
    availableFrom: "Ngày có thể cho thuê",
    tenantType: "Đối tượng thuê",

    // Other
    constructionYear: "Năm xây dựng/bàn giao",
    notes: "Ghi chú",
  };

  // Format value based on field type
  const formatValue = (key, value) => {
    if (!value) return null;

    // Property type translation
    if (key === "propertyType") {
      return (
        PROPERTY_TYPE_LABELS[value] ||
        (isRental ? value : PROPERTY_TYPE_LABELS[PROPERTY_TYPES.OTHER_PROPERTY])
      );
    }

    // Area fields - add m² unit
    if (
      key === "area" ||
      key === "landArea" ||
      key === "constructionArea"
    ) {
      return `${value} m²`;
    }

    // Length fields - add m unit
    if (key === "landLength" || key === "frontage") {
      return `${value} m`;
    }

    // Year field
    if (key === "constructionYear") {
      return value;
    }

    return value;
  };

  // Group fields by category
  const renderFieldGroup = (title, fields) => {
    const visibleFields = fields
      .map((key) => {
        const value = formatValue(key, formData[key]);
        if (!value) return null;

        return (
          <div key={key} className="flex border-b border-gray-100 py-2">
            <span className="font-medium text-gray-700 w-1/3 flex-shrink-0">
              {fieldLabels[key]}:
            </span>
            <span className="text-gray-900 w-2/3">{value}</span>
          </div>
        );
      })
      .filter(Boolean);

    if (visibleFields.length === 0) return null;

    return (
      <div className="mb-4">
        <h4 className="text-sm font-semibold text-gray-600 uppercase mb-2">
          {title}
        </h4>
        <div className="space-y-1">{visibleFields}</div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Property Type & Location */}
      {renderFieldGroup("Thông tin cơ bản", [
        "propertyType",
        "apartmentType",
        "houseType",
        "landType",
        "propertyTypeOther",
        "address",
        "projectName",
        "building",
        "floor",
        "apartmentCode",
      ])}

      {/* Dimensions */}
      {renderFieldGroup("Diện tích & Kích thước", [
        "area",
        "landArea",
        "constructionArea",
        "landLength",
        "frontage",
      ])}

      {/* Room details */}
      {renderFieldGroup("Chi tiết phòng", [
        "bedrooms",
        "bathrooms",
        "floors",
      ])}

      {/* Direction */}
      {renderFieldGroup("Hướng", [
        "balconyDirection",
        "doorDirection",
        "landDirection",
        "direction",
      ])}

      {/* Legal & Status */}
      {renderFieldGroup("Tình trạng pháp lý & Nội thất", [
        "legalStatus",
        "furnitureStatus",
      ])}

      {/* Goals & Tenant (for rental) */}
      {isRental
        ? renderFieldGroup("Thông tin cho thuê", [
            "rentalGoal",
            "tenantType",
            "availableFrom",
          ])
        : renderFieldGroup("Mục tiêu", ["saleGoal"])}

      {/* Other information */}
      {renderFieldGroup("Thông tin khác", ["constructionYear", "notes"])}
    </div>
  );
};

export default PropertyFormDataDisplay;
